import {
  isFormControlReadyForAsyncValidation,
  removeErrorsFromFormControl,
  addErrorsToFormControl,
} from './../../dg_core/pre-login/pre-login.util';
import { Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import {
  AsyncValidatorFn,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  getValidationsForPolicies,
  PasswordPolicy,
  PasswordPolicyComponent,
  PasswordPolicyStatus,
} from 'dg-password-policy-lib';
import { concat, of, Subscription } from 'rxjs';
import { map, catchError, toArray } from 'rxjs/operators';
import { PreLoginService } from '../../dg_core/pre-login/pre-login.service';
import { EncryptionService } from '../../services/encryption.service';
import { DgPasswordPolicyWrapperService } from './dg-password-policy-wrapper.service';

export interface PolicyVO {
  key: string;
  value: string;
  message: string;
  activeFlag: number;
  policyOrder: number;
  langId: number;
  className: string;
}

interface GenericObject {
  [key: string]: any;
}

@Component({
  selector: 'dg-password-policy-wrapper',
  templateUrl: './dg-password-policy-wrapper.component.html',
  styleUrls: ['./dg-password-policy-wrapper.component.scss'],
})
export class DgPasswordPolicyWrapperComponent implements OnInit, OnDestroy {
  @Input() inputRef: HTMLInputElement;
  @Input() isPrelogin = false;
  @Input() salt: string;
  @Input() control: FormControl;
  @Input() existingValidators: ValidatorFn[] = [];
  @Input() existingAsyncValidators: AsyncValidatorFn[] = [];

  policies: PasswordPolicy[] = [];
  asyncPolicies: PasswordPolicy[] = [];
  asyncErrors: ValidationErrors;
  controlValueChangesSubscription: Subscription;
  controlStatusChangesSubscription: Subscription;

  @ViewChild('dgPasswordPolicy') dgPasswordPolicy: PasswordPolicyComponent;

  isPassowrdValid: boolean = false;
  validPwd: string = null;

  constructor(
    private service: DgPasswordPolicyWrapperService,
    private preLoginService: PreLoginService,
    private encryptionService: EncryptionService
  ) {}

  ngOnInit(): void {
    this.service.getPasswordPolicies().subscribe((policies: PolicyVO[]) => {
      this.policies = this.preparePoliciesReborn(policies);
      this.asyncPolicies = this.policies.filter((policy) => policy.async);
      this.asyncErrors = this.asyncPolicies.reduce(
        (prev, curr) => ({ ...prev, [curr.id]: true }),
        {}
      );

      //
      this.controlValueChangesSubscription = this.control.valueChanges.subscribe(
        (value) => {
          setTimeout(() => {
            if (this.validPwd && this.validPwd === value) {
              this.makePassowrdValid(value);
              this.checkPolicyStatus();
            } else {
              this.makePassowrdInvalid();
              this.setAsyncErrors();
            }
          }, 0);
        }
      );

      /* this.controlStatusChangesSubscription = this.control.statusChanges.subscribe(
        () => {
          this.checkPolicyStatus();
        }
      ); */

      this.setAsyncErrors();
      //

      const { validators, asyncValidators } = this.prepareValidators();

      this.control.setValidators(validators);
      this.control.setAsyncValidators(asyncValidators);
      this.control.updateValueAndValidity();

      this.inputRef?.addEventListener('blur', this.onInputBlur);
    });
  }

  ngOnDestroy(): void {
    this.inputRef?.removeEventListener('blur', this.onInputBlur);
    this.controlValueChangesSubscription?.unsubscribe();
    this.controlStatusChangesSubscription?.unsubscribe();
  }

  preparePolicies(policies: PolicyVO[]): PasswordPolicy[] {
    policies.sort(
      (policy1, policy2) => policy1.policyOrder - policy2.policyOrder
    );

    return policies.map((policy) => ({
      async: policy.className,
      fulfilled: PasswordPolicyStatus.intermediate,
      id: policy.key,
      policyText: policy.message,
      validations: [
        {
          async: policy.className ? true : false,
          type: policy.key,
          value: policy.className
            ? (((control: FormControl) => {
                const { cipherText, salt } = this.encryptionService.encrypt(
                  control.value,
                  this.salt
                );
                return this.service
                  .validateAsyncValidations(
                    {
                      className: policy.className,
                      newPassword: cipherText,
                      pwdPolicyKey: policy.key,
                      saltKey: salt,
                      userName: this.isPrelogin
                        ? this.preLoginService?.preLoginUsername
                        : undefined,
                    },
                    this.isPrelogin
                  )
                  .pipe(
                    map((response) => {
                      if (response?.valid) return null;
                      else return { [policy.key]: true };
                    })
                  )
                  .toPromise();
              }) as AsyncValidatorFn)
            : (((control: FormControl) =>
                !control?.value ||
                !new RegExp(policy.value)?.test(control?.value)
                  ? { pattern: true }
                  : null) as ValidatorFn),
          errorMessage: '',
        },
      ],
    }));
  }

  preparePoliciesReborn(policies: PolicyVO[]): PasswordPolicy[] {
    policies.sort(
      (policy1, policy2) => policy1.policyOrder - policy2.policyOrder
    );

    return policies.map((policy) => ({
      async: policy.className,
      fulfilled: PasswordPolicyStatus.intermediate,
      id: policy.key,
      policyText: policy.message,
      validations: !policy.className
        ? [
            {
              async: false,
              type: policy.key,
              value: ((control: FormControl) => {
                addErrorsToFormControl(control, this.asyncErrors);
                return !control?.value ||
                  !new RegExp(policy.value)?.test(control?.value)
                  ? { pattern: true }
                  : null;
              }) as ValidatorFn,
              errorMessage: '',
            },
          ]
        : [],
    }));
  }

  prepareValidators(): {
    validators: ValidatorFn[];
    asyncValidators: AsyncValidatorFn[];
  } {
    let validators: ValidatorFn[] = getValidationsForPolicies(
      this.policies.filter((policy) => !policy.async)
    );

    if (!validators?.length) validators = [Validators.required];
    else validators.push(Validators.required);

    let asyncValidators: AsyncValidatorFn[] = getValidationsForPolicies(
      this.policies.filter((policy) => policy.async),
      true
    ) as AsyncValidatorFn[];

    validators = [...this.existingValidators, ...validators];
    asyncValidators = [...this.existingAsyncValidators, ...asyncValidators];
    return { validators, asyncValidators };
  }

  onInputBlur = (e) => {
    // perform async validations
    // addErrorsToFormControl(this.control, this.asyncErrors);
    if (this.isPassowrdValid) {
      return;
    }
    if (!isFormControlReadyForAsyncValidation(this.control, this.asyncErrors)) {
      return;
    }
    const { cipherText, salt } = this.encryptionService.encrypt(
      this.control.value,
      this.salt
    );

    const obs = this.asyncPolicies.map((policy) =>
      this.service
        .validateAsyncValidations(
          {
            className: policy.async,
            newPassword: cipherText,
            pwdPolicyKey: policy.id,
            saltKey: salt,
            userName: this.isPrelogin
              ? this.preLoginService?.preLoginUsername
              : undefined,
          },
          this.isPrelogin
        )
        .pipe(
          map((data) => ({ policy, ...data })),
          catchError(() => of(null))
        )
    );
    concat(...obs)
      .pipe(toArray())
      .subscribe(
        (
          data: { valid: boolean; policy: PasswordPolicy; [key: string]: any }[]
        ) => {
          console.log('%cCompleted: ', 'color: orange;font-size: 3em', data);
          data?.forEach((item) => {
            if (item?.valid) {
              removeErrorsFromFormControl(this.control, {
                [item?.policy?.id]: true,
              });
            } else {
              addErrorsToFormControl(this.control, {
                [item?.policy?.id]: true,
              });
            }
          });

          this.checkPolicyStatus();

          if (
            !this.control?.errors ||
            !Object.keys(this.control?.errors)?.length
          ) {
            this.makePassowrdValid(this.control.value);
          } else {
            this.makePassowrdInvalid();
          }
        },
        (error) => {
          console.error(error);
          this.makePassowrdInvalid();
        }
      );
  };

  makePassowrdValid(password: string) {
    this.isPassowrdValid = true;
    this.validPwd = password;
  }

  makePassowrdInvalid() {
    this.isPassowrdValid = false;
  }

  setAsyncErrors() {
    addErrorsToFormControl(this.control, this.asyncErrors);
    this.checkPolicyStatus();
  }

  checkPolicyStatus() {
    this.dgPasswordPolicy?.checkPolicyStatus();
  }
}
